import { useState } from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../../constants/theme';

export interface BreakdownRow {
  label: string;
  modifier?: string;
  value: string;
  isBase?: boolean;
  isTotal?: boolean;
  link?: string;         // React Router path for cross-page navigation
  modifierColor?: string; // e.g., COLORS.positive or COLORS.negative
}

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  color?: string;
  breakdown?: BreakdownRow[];
  borderless?: boolean;
}

export function StatCard({ label, value, sub, color, breakdown, borderless }: StatCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isExpandable = breakdown !== undefined && breakdown.length > 0;

  const handleToggle = () => {
    if (isExpandable) setIsExpanded((prev) => !prev);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isExpandable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setIsExpanded((prev) => !prev);
    }
  };

  return (
    <div
      className="rounded"
      style={borderless ? {} : { backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
    >
      <div
        className={`p-3 flex flex-col gap-0.5${isExpandable ? ' cursor-pointer select-none' : ''}`}
        role={isExpandable ? 'button' : undefined}
        tabIndex={isExpandable ? 0 : undefined}
        aria-expanded={isExpandable ? isExpanded : undefined}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>
            {label}
          </span>
          {isExpandable && (
            <span
              className="text-xs transition-transform duration-200"
              style={{
                color: COLORS.text.tertiary,
                display: 'inline-block',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              ▾
            </span>
          )}
        </div>
        <span
          className="text-lg font-mono font-semibold leading-tight"
          style={{ color: color ?? COLORS.text.primary }}
        >
          {value}
        </span>
        {sub && (
          <span className="text-xs" style={{ color: COLORS.text.tertiary }}>
            {sub}
          </span>
        )}
      </div>

      {isExpandable && isExpanded && (
        <div
          className="border-t text-xs"
          style={{ borderColor: COLORS.border }}
        >
          <table className="w-full">
            <tbody>
              {breakdown!.map((row, idx) => (
                <tr
                  key={idx}
                  style={{
                    backgroundColor: row.isTotal ? COLORS.sectionHeader : undefined,
                    borderBottom: idx < breakdown!.length - 1 ? `1px solid ${COLORS.border}` : undefined,
                    fontWeight: row.isTotal || row.isBase ? 500 : undefined,
                  }}
                >
                  <td className="py-1.5 px-3" style={{ color: COLORS.text.secondary }}>
                    {row.link ? (
                      <Link
                        to={row.link}
                        className="hover:underline"
                        style={{ color: COLORS.link }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {row.label}
                      </Link>
                    ) : (
                      row.label
                    )}
                  </td>
                  {row.modifier !== undefined && (
                    <td
                      className="py-1.5 px-3 text-right font-mono"
                      style={{ color: row.modifierColor ?? COLORS.text.tertiary }}
                    >
                      {row.modifier}
                    </td>
                  )}
                  <td
                    className="py-1.5 px-3 text-right font-mono"
                    style={{ color: row.isTotal ? COLORS.text.primary : COLORS.text.secondary }}
                  >
                    {row.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
